import { useState } from "react";
import { useNavigate } from "react-router-dom";
import TopNav from "../home/components/TopNav";

const features = [
  {
    icon: "ri-map-2-line",
    title: "地图实时查看",
    desc: "随时随地打开地图，查看小区、园区、商铺图层，支持缩放与图层切换，数据实时同步。",
    color: "bg-cyan-50 text-cyan-600",
  },
  {
    icon: "ri-search-eye-line",
    title: "快速搜索查询",
    desc: "输入地名或关键词即可定位目标区域，支持按类型筛选，一键查看卡片详情。",
    color: "bg-emerald-50 text-emerald-600",
  },
  {
    icon: "ri-download-cloud-2-line",
    title: "数据离线下载",
    desc: "将常用区域数据离线缓存，无需联网也可查阅，支持 Excel/CSV 快速导出。",
    color: "bg-amber-50 text-amber-600",
  },
  {
    icon: "ri-notification-3-line",
    title: "数据更新推送",
    desc: "订阅关注区域，有数据变更时第一时间推送通知，掌握最新动态。",
    color: "bg-rose-50 text-rose-600",
  },
  {
    icon: "ri-folder-chart-line",
    title: "个人空间同步",
    desc: "与网页端数据空间完全同步，导出记录、收藏区域跨端一致，随时继续上次操作。",
    color: "bg-violet-50 text-violet-600",
  },
  {
    icon: "ri-shield-user-line",
    title: "安全多账户",
    desc: "支持企业多账户管理，角色权限细粒度控制，安全登录，数据不外泄。",
    color: "bg-slate-100 text-slate-600",
  },
];

const screenshots = [
  {
    label: "地图图层",
    img: "https://readdy.ai/api/search-image?query=mobile%20phone%20screen%20showing%20a%20GIS%20map%20application%20with%20colorful%20district%20polygon%20overlays%20and%20location%20pin%20markers%20on%20dark%20map%20tiles%2C%20clean%20minimalist%20UI%20with%20a%20bottom%20navigation%20bar%2C%20professional%20data%20visualization%20app%20interface%2C%20small%20card%20popup%20showing%20building%20details%2C%20dark%20slate%20background%20map%20with%20emerald%20and%20cyan%20accent%20colors&width=200&height=360&seq=mobile-screen-map-v1&orientation=portrait",
  },
  {
    label: "数据卡片",
    img: "https://readdy.ai/api/search-image?query=mobile%20app%20screen%20showing%20a%20data%20card%20detail%20view%20with%20statistics%20grid%20rows%20of%20property%20information%2C%20building%20name%20address%20floor%20count%20household%20number%20in%20a%20clean%20white%20card%20layout%2C%20professional%20business%20data%20application%2C%20top%20navigation%20bar%20with%20back%20button%2C%20subtle%20shadow%20card%20design%20on%20light%20gray%20background%2C%20small%20charts%20and%20icons%20alongside%20data%20rows&width=200&height=360&seq=mobile-screen-card-v2&orientation=portrait",
  },
  {
    label: "数据空间",
    img: "https://readdy.ai/api/search-image?query=mobile%20app%20screen%20displaying%20a%20file%20manager%20data%20space%20interface%20with%20a%20list%20of%20exported%20dataset%20files%20showing%20file%20names%20types%20sizes%20and%20dates%2C%20download%20and%20preview%20action%20buttons%2C%20storage%20usage%20progress%20bar%20at%20top%2C%20clean%20white%20background%20with%20blue%20navy%20accent%20color%20scheme%2C%20professional%20enterprise%20data%20management%20mobile%20application&width=200&height=360&seq=mobile-screen-space-v3&orientation=portrait",
  },
];

export default function MobilePage() {
  const navigate = useNavigate();
  const [activeScreen, setActiveScreen] = useState(0);

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Noto Sans SC', sans-serif" }}>
      <TopNav />

      {/* Hero */}
      <section
        className="relative overflow-hidden pt-24 pb-24"
        style={{ background: "linear-gradient(135deg, #0f2460 0%, #1a3a8a 60%, #1e3a6a 100%)" }}
      >
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage:
              "radial-gradient(circle, rgba(100,200,255,0.4) 1px, transparent 1px)",
            backgroundSize: "32px 32px",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-8 flex items-center gap-16">
          {/* Left */}
          <div className="flex-1 min-w-0">
            <div className="inline-flex items-center gap-2 bg-cyan-400/10 border border-cyan-400/30 rounded-full px-4 py-1.5 mb-6">
              <i className="ri-smartphone-line text-cyan-300 text-sm"></i>
              <span className="text-cyan-300 text-xs font-medium tracking-wide">iOS &amp; Android · 随时随地 · 数据随身</span>
            </div>
            <h1 className="text-4xl font-bold text-white leading-tight mb-4">
              空天数源 移动端
              <span className="block text-xl font-normal text-cyan-300 mt-2">Mobile App · Always On</span>
            </h1>
            <p className="text-blue-100/80 text-base leading-relaxed mb-8 max-w-lg">
              将空间数据能力装进口袋。无论在外勘察还是出差途中，都能实时查看地图图层、查询区域数据、管理个人数据空间，与网页端完全同步。
            </p>

            {/* Platform badges */}
            <div className="flex items-center gap-4 mb-10">
              <button className="flex items-center gap-3 bg-white/10 hover:bg-white/15 border border-white/25 text-white px-5 py-3 rounded-xl transition-all cursor-pointer whitespace-nowrap">
                <div className="w-7 h-7 flex items-center justify-center">
                  <i className="ri-apple-line text-2xl"></i>
                </div>
                <div className="text-left">
                  <div className="text-xs text-white/60">下载于</div>
                  <div className="text-sm font-semibold">App Store</div>
                </div>
              </button>
              <button className="flex items-center gap-3 bg-white/10 hover:bg-white/15 border border-white/25 text-white px-5 py-3 rounded-xl transition-all cursor-pointer whitespace-nowrap">
                <div className="w-7 h-7 flex items-center justify-center">
                  <i className="ri-android-line text-2xl text-emerald-300"></i>
                </div>
                <div className="text-left">
                  <div className="text-xs text-white/60">下载于</div>
                  <div className="text-sm font-semibold">Google Play</div>
                </div>
              </button>
            </div>

            <div className="flex items-center gap-10">
              {[["50,000+", "下载量"], ["4.8", "App 评分"], ["iOS 14+", "系统支持"]].map(([val, label]) => (
                <div key={label}>
                  <div className="text-2xl font-bold text-cyan-300">{val}</div>
                  <div className="text-blue-200/60 text-xs mt-0.5">{label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: QR codes + mockup */}
          <div className="flex-shrink-0 flex flex-col items-center gap-6">
            {/* Phone mockup area */}
            <div className="relative">
              <div className="w-52 h-96 rounded-3xl border-4 border-white/20 overflow-hidden bg-slate-900 relative">
                <img
                  src={screenshots[activeScreen].img}
                  alt={screenshots[activeScreen].label}
                  className="w-full h-full object-cover object-top"
                />
                {/* Screen indicator dots */}
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                  {screenshots.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setActiveScreen(i)}
                      className={`w-1.5 h-1.5 rounded-full transition-all cursor-pointer ${activeScreen === i ? "bg-white w-4" : "bg-white/40"}`}
                    />
                  ))}
                </div>
              </div>
              {/* Phone notch */}
              <div className="absolute top-3 left-1/2 -translate-x-1/2 w-16 h-4 bg-slate-900 rounded-full border border-white/10"></div>
            </div>

            {/* Screen label tabs */}
            <div className="flex gap-2">
              {screenshots.map((s, i) => (
                <button
                  key={s.label}
                  onClick={() => setActiveScreen(i)}
                  className={`text-xs px-3 py-1.5 rounded-full cursor-pointer transition-all whitespace-nowrap ${activeScreen === i ? "bg-cyan-400 text-[#0f2460] font-semibold" : "bg-white/10 text-white/70 hover:bg-white/15"}`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* QR code download section */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-5xl mx-auto px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl font-bold text-slate-900 mb-2">扫码即可下载</h2>
            <p className="text-slate-500 text-sm">使用手机扫描二维码，即可跳转至对应应用商店下载</p>
          </div>
          <div className="grid grid-cols-2 gap-8 max-w-xl mx-auto">
            {[
              { platform: "iOS 版", store: "App Store", icon: "ri-apple-line", color: "text-slate-800", bg: "bg-slate-900", label: "iPhone / iPad" },
              { platform: "Android 版", store: "Google Play & 应用宝", icon: "ri-android-line", color: "text-emerald-400", bg: "bg-emerald-700", label: "Android 手机" },
            ].map((p) => (
              <div key={p.platform} className="bg-white border border-slate-200 rounded-2xl p-8 text-center">
                <div className={`w-12 h-12 flex items-center justify-center rounded-xl mx-auto mb-4 ${p.bg}`}>
                  <i className={`${p.icon} text-2xl ${p.color}`}></i>
                </div>
                <h3 className="font-bold text-slate-800 mb-1">{p.platform}</h3>
                <p className="text-xs text-slate-500 mb-5">{p.store} · {p.label}</p>
                {/* QR placeholder */}
                <div className="w-36 h-36 mx-auto bg-slate-100 border-2 border-dashed border-slate-300 rounded-xl flex flex-col items-center justify-center gap-2 mb-4">
                  <i className="ri-qr-code-line text-4xl text-slate-400"></i>
                  <span className="text-xs text-slate-400">二维码</span>
                </div>
                <button className={`w-full py-2.5 rounded-xl text-sm font-semibold cursor-pointer transition-colors whitespace-nowrap ${p.bg} text-white hover:opacity-90`}>
                  点击下载
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl font-bold text-slate-900 mb-2">核心功能</h2>
            <p className="text-slate-500 text-sm">专为移动场景深度优化，不只是网页版的缩小版</p>
          </div>
          <div className="grid grid-cols-3 gap-6">
            {features.map((f) => (
              <div key={f.title} className="bg-slate-50 border border-slate-100 rounded-2xl p-6">
                <div className={`w-11 h-11 flex items-center justify-center rounded-xl mb-4 ${f.color}`}>
                  <i className={`${f.icon} text-xl`}></i>
                </div>
                <h3 className="font-bold text-slate-800 mb-2">{f.title}</h3>
                <p className="text-slate-500 text-sm leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* System requirements */}
      <section className="py-12 bg-slate-50">
        <div className="max-w-5xl mx-auto px-8">
          <h2 className="text-lg font-bold text-slate-800 mb-5 text-center">系统要求</h2>
          <div className="grid grid-cols-2 gap-6">
            {[
              { platform: "iOS", icon: "ri-apple-line", items: ["iPhone / iPad", "iOS 14.0 及以上", "约 85 MB 安装包", "支持 Face ID 快速登录"] },
              { platform: "Android", icon: "ri-android-line", items: ["Android 8.0 及以上", "约 78 MB 安装包", "支持指纹快速登录", "支持 HarmonyOS 4.0"] },
            ].map((s) => (
              <div key={s.platform} className="bg-white border border-slate-200 rounded-xl p-6 flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-slate-100 flex-shrink-0">
                  <i className={`${s.icon} text-xl text-slate-600`}></i>
                </div>
                <div>
                  <h4 className="font-bold text-slate-800 mb-3">{s.platform} 版</h4>
                  <ul className="space-y-2">
                    {s.items.map((item) => (
                      <li key={item} className="flex items-center gap-2 text-sm text-slate-600">
                        <i className="ri-check-line text-emerald-400 flex-shrink-0"></i>{item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 py-10">
        <div className="max-w-7xl mx-auto px-8 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <i className="ri-earth-fill text-cyan-300 text-xl"></i>
            <span className="text-white font-bold">空天数源</span>
          </div>
          <div className="flex items-center gap-6">
            <button onClick={() => navigate("/")} className="text-slate-400 hover:text-white text-sm cursor-pointer">首页</button>
            <button onClick={() => navigate("/api-service")} className="text-slate-400 hover:text-white text-sm cursor-pointer">API服务</button>
            <button onClick={() => navigate("/personal-space")} className="text-slate-400 hover:text-white text-sm cursor-pointer">数据空间</button>
          </div>
          <p className="text-slate-500 text-xs">&copy; 2024 空天数源. 保留所有权利。</p>
        </div>
      </footer>
    </div>
  );
}
